import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { apiService } from '../services/apiService';

/**
 * Clés alignées sur la table PostgreSQL ``FEATURE_FLAG`` (API ``GET /feature-flags``).
 */
export enum FeatureFlag {
  CHAT_FEATURE = 'CHAT_FEATURE',
  /** Assistant pros — clé LAIKA */
  LAIKA = 'LAIKA',
  /** Module pension / hébergement — clé BOARDING_MODULE */
  BOARDING_MODULE = 'BOARDING_MODULE',
  NOTES_TEMPLATES = 'NOTES_TEMPLATES',
  NOTES_AI_ASSIST = 'NOTES_AI_ASSIST',
  NOTES_ATTACHMENTS = 'NOTES_ATTACHMENTS',
  /** Widget de réservation (Settings) — default ON ; `false` = kill-switch. */
  BOOKING_WIDGET = 'BOOKING_WIDGET',
  /** Facturation électronique réglementaire via SUPER PDP (réforme française). */
  EFACTURATION = 'EFACTURATION',
  /**
   * Module facturation backoffice (page Factures / Devis / détail).
   * Default ON ; `false` = kill-switch avec message de travaux (nav reste visible).
   */
  BILLING_MODULE = 'BILLING_MODULE',
  /** Accès global au backoffice pro — désactiver = page Mise à jour. */
  BACKOFFICE_ACCESS = 'BACKOFFICE_ACCESS',
  /** Segments clients + auto-validation réservations (GA — kill-switch). */
  CLIENT_SEGMENTS_AUTO_VALIDATE = 'CLIENT_SEGMENTS_AUTO_VALIDATE',
  /**
   * Offre essai inscription (14j bonus Premium, /app/offre-essai).
   * Default OFF — activer depuis Rantanplan → Feature flags.
   */
  SIGNUP_TRIAL_OFFER = 'SIGNUP_TRIAL_OFFER',
  /**
   * Programme ambassadeur self-serve (code auto + contrat).
   * Default ON ; `false` = kill-switch.
   */
  AMBASSADOR = 'AMBASSADOR',
  /**
   * Connecteurs IA Pro (MCP — Claude / ChatGPT / Grok…).
   * Default OFF — activer depuis Rantanplan → Feature flags.
   * UI Settings : aussi limitée aux bêta-testeurs.
   */
  PRO_MCP_CONNECTORS = 'PRO_MCP_CONNECTORS',
}

/**
 * Flags désactivés par défaut tant que la clé n'a pas été chargée OU si la clé
 * est absente côté API : utiles pour les fonctionnalités en bêta / déploiement progressif.
 *
 * Contrat zéro-régression : les features maturées (widget, notes sous-flags, messagerie,
 * e-facturation, facturation…) NE doivent PAS être ici — absentes/true = comportement historique ON.
 * Seul `enabled: false` explicite en DB/SSM coupe.
 */
const DEFAULT_OFF_FLAGS: ReadonlySet<FeatureFlag> = new Set<FeatureFlag>([
  FeatureFlag.LAIKA,
  FeatureFlag.SIGNUP_TRIAL_OFFER,
  FeatureFlag.PRO_MCP_CONNECTORS,
]);

type LoadStatus = 'idle' | 'loaded' | 'error';

/**
 * Résolution pure (testable) : absent / non chargé → ON sauf DEFAULT_OFF ;
 * `false` explicite → OFF ; sinon ON.
 */
export function resolveFeatureFlagEnabled(
  featureName: FeatureFlag,
  remoteFlags: Record<string, boolean>,
  status: LoadStatus,
): boolean {
  const isDefaultOff = DEFAULT_OFF_FLAGS.has(featureName);
  if (status !== 'loaded') {
    return !isDefaultOff;
  }
  const v = remoteFlags[featureName];
  if (v === undefined) {
    return !isDefaultOff;
  }
  return v !== false;
}

interface FeatureFlagsResponse {
  flags?: Record<string, boolean>;
}

interface FeatureFlagsContextType {
  isFeatureEnabled: (featureName: FeatureFlag) => boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

const FEATURE_FLAGS_CACHE_KEY = 'feature-flags-cache';
const FEATURE_FLAGS_CACHE_TTL_MS = 5 * 60 * 1000;

function readFeatureFlagsCache(): Record<string, boolean> | null {
  try {
    const raw = localStorage.getItem(FEATURE_FLAGS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { flags?: Record<string, boolean>; ts?: number };
    if (!parsed.ts || Date.now() - parsed.ts >= FEATURE_FLAGS_CACHE_TTL_MS) {
      return null;
    }
    return parsed.flags ?? {};
  } catch {
    return null;
  }
}

function writeFeatureFlagsCache(flags: Record<string, boolean>) {
  try {
    localStorage.setItem(
      FEATURE_FLAGS_CACHE_KEY,
      JSON.stringify({ flags, ts: Date.now() })
    );
  } catch {
    /* navigation privée / quota */
  }
}

export const FeatureFlagsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const cachedFlags = readFeatureFlagsCache();
  const [remoteFlags, setRemoteFlags] = useState<Record<string, boolean>>(cachedFlags ?? {});
  const [status, setStatus] = useState<LoadStatus>(cachedFlags ? 'loaded' : 'idle');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.get<FeatureFlagsResponse>('/feature-flags', {
          timeout: 8000,
        });
        if (!cancelled) {
          const flags = res.data?.flags ?? {};
          setRemoteFlags(flags);
          setStatus('loaded');
          writeFeatureFlagsCache(flags);
        }
      } catch {
        if (!cancelled) {
          if (!cachedFlags) {
            setRemoteFlags({});
            setStatus('error');
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const isFeatureEnabled = useCallback(
    (featureName: FeatureFlag): boolean => {
      /** Override local uniquement — ne jamais activer via variable d'env en prod/staging. */
      if (
        featureName === FeatureFlag.LAIKA &&
        process.env.REACT_APP_FORCE_LAIKA_ENABLED === 'true'
      ) {
        return true;
      }
      return resolveFeatureFlagEnabled(featureName, remoteFlags, status);
    },
    [status, remoteFlags],
  );

  return (
    <FeatureFlagsContext.Provider
      value={{
        isFeatureEnabled,
      }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
};
